import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import { Box, Stack, useTheme } from "@vapor/react-material";
import dayjs from "dayjs";
import type { FulfilmentActivityDto } from "../../../../../core/api/use-get-fulfilment-activities/FulfilmentActivityDto";
import { useGetFulfilmentActivities } from "../../../../../core/api/use-get-fulfilment-activities/useGetFulfilmentActivities";
import type { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { FulfilmentActivity } from "../fulfilment-activity/FulfilmentActivity";

interface FulfilmentActivityTimelineProps {
    fulfilmentStatus: FulfilmentStatus;
    levyId: string;
    onClickActivityEditTitle: (activity: FulfilmentActivityDto) => void;
    onClickActivityViewNote: (activity: FulfilmentActivityDto) => void;
    onUpdateActivity: () => void;
    taxPayerWorkspaceId: string;
    width?: string | number;
    year: number;
}

export const FulfilmentActivityTimeline = ({
    fulfilmentStatus,
    levyId,
    onClickActivityEditTitle,
    onClickActivityViewNote,
    onUpdateActivity,
    taxPayerWorkspaceId,
    year,
}: FulfilmentActivityTimelineProps): JSX.Element => {
    const theme = useTheme();

    const { data: activities, isFetching } = useGetFulfilmentActivities({
        taxPayerWorkspaceId,
        levyId,
        year,
    });

    return (
        <LoadingWrapper loading={isFetching} variant="skeleton-list">
            <Stack>
                {[...activities]
                    .sort((activity1, activity2) =>
                        dayjs(activity2.timestamp).diff(
                            dayjs(activity1.timestamp),
                        ),
                    )
                    .map((activity, index) => {
                        return (
                            <Stack key={activity.id}>
                                <FulfilmentActivity
                                    activity={activity}
                                    fulfilmentStatus={fulfilmentStatus}
                                    levyId={levyId}
                                    onClickActivityEditTitle={
                                        onClickActivityEditTitle
                                    }
                                    onClickActivityViewNote={
                                        onClickActivityViewNote
                                    }
                                    onUpdateActivity={onUpdateActivity}
                                    taxPayerWorkspaceId={taxPayerWorkspaceId}
                                    year={year}
                                />
                                {activities.length > index + 1 && (
                                    <Box
                                        sx={{
                                            marginLeft: 3,
                                            minHeight: "24px",
                                            height: "100%",
                                            width: "1px",
                                            backgroundColor:
                                                theme.palette.primary
                                                    .columbiaBlue,
                                        }}
                                    />
                                )}
                            </Stack>
                        );
                    })}
            </Stack>
        </LoadingWrapper>
    );
};
